import { escapeHtml } from '../format';
import { INTAKE_EXPIRY_GRACE_MS, type IntakeSession, type IntakeStatusUpdate } from '../intake-client';
import type { IntakeFailure } from '../types';
import { trackKrc } from './analytics';
import {
  EMAIL_ELSEWHERE_ACTION,
  EMAIL_ELSEWHERE_NOTE,
  EMAIL_SENT_COPY_TIP,
  EMAIL_STEPS,
  INTAKE_EXPIRED_MESSAGE,
  intakeFailureCopy,
  intakeRemainingLabel,
  intakeStorageNote,
  type GuideStep,
} from './copy';
import { copyText, selectElementText } from './dom';
import type { PhonePlatform } from './environment';
import { icon } from './icons';
import {
  classifyIntakeError,
  discardIntakeAddress,
  intakeErrorMessage,
  type IntakeApi,
  type IntakeErrorKind,
  type StoredIntake,
} from './intake-api';
import { createIntakePoller, documentVisibility, type IntakePoller, type PollerVisibility } from './intake-poller';
import type { Toast } from './toast';

/**
 * Card B on the start screen: "휴대폰 카톡에서 메일로 보내기" (spec §7.1).
 * Issues an address, shows the export steps, waits for the e-mail (polling
 * every 3 s while visible), resumes a stored session and reports failures.
 * The address expires here at its local deadline (plus a short grace) even
 * when the server cannot be reached, and only one open tab uses an address.
 */

export interface EmailCardOptions {
  readonly api: IntakeApi;
  readonly platform: PhonePlatform;
  readonly featured: boolean;
  readonly toast: Toast;
  /** Called once when the files arrived; the app downloads them (this page keeps holding the address). */
  readonly onReceived: (session: IntakeSession, status: IntakeStatusUpdate) => void;
  readonly visibility?: PollerVisibility;
  readonly now?: () => number;
}

export interface EmailCard {
  dispose(): void;
}

type CardState =
  /** `elsewhere`: an address another open tab took over from this one. */
  | { readonly kind: 'idle'; readonly elsewhere: IntakeSession | null }
  | { readonly kind: 'creating' }
  /** `remembered`: false when this browser could not store the address, so a reload loses it. */
  | { readonly kind: 'waiting'; readonly session: IntakeSession; readonly remembered: boolean }
  | { readonly kind: 'expired' }
  | { readonly kind: 'error'; readonly message: string };

type FocusTarget = 'heading' | 'address' | 'primary' | null;

const IDLE: CardState = { kind: 'idle', elsewhere: null };
const PLATFORMS: readonly { readonly id: PhonePlatform; readonly label: string }[] = [
  { id: 'iphone', label: 'iPhone' },
  { id: 'android', label: 'Android' },
];

/** Lets long addresses wrap after the @ instead of mid-word. */
function addressMarkup(address: string): string {
  const at = address.indexOf('@');
  if (at < 0) return escapeHtml(address);
  return `${escapeHtml(address.slice(0, at + 1))}<wbr>${escapeHtml(address.slice(at + 1))}`;
}

function stepsMarkup(steps: readonly GuideStep[]): string {
  return `<ol class="krc-steps">${steps.map((step, index) => `
    <li><span class="krc-step-number" aria-hidden="true">${index + 1}</span><div><strong>${escapeHtml(step.title)}</strong>${step.detail ? `<p>${escapeHtml(step.detail)}</p>` : ''}</div></li>`).join('')}
  </ol>`;
}

/** krc_intake_failed reason for a failed POST /sessions. */
function createFailureReason(kind: IntakeErrorKind): string {
  if (kind === 'rate-limited') return 'RATE_LIMITED';
  if (kind === 'busy') return 'BUSY';
  return 'CREATE_FAILED';
}

export function mountEmailCard(container: HTMLElement, options: EmailCardOptions): EmailCard {
  const api = options.api;
  const now = options.now ?? Date.now;
  const visibility = options.visibility ?? documentVisibility();
  let state: CardState = IDLE;
  let platform: PhonePlatform = options.platform;
  let poller: IntakePoller | null = null;
  let ticker = 0;
  let createController: AbortController | null = null;
  /** The waiting address's deadline on this device's clock, refreshed by every status answer. */
  let deadline = 0;
  let failure: IntakeFailure | null = null;
  let disposed = false;

  const headerMarkup = () => `
    <div class="krc-card-head">
      <span class="krc-card-icon">${icon('mail')}</span>
      <span class="krc-badge${options.featured ? '' : ' is-quiet'}">${options.featured ? '휴대폰 추천' : '휴대폰에서'}</span>
    </div>
    <h3 id="krc-email-title" tabindex="-1">휴대폰 카톡에서 메일로 보내기</h3>`;

  function bodyMarkup(): string {
    const primary = options.featured ? 'button-primary' : 'button-soft';
    switch (state.kind) {
      case 'idle':
      case 'creating': {
        const busy = state.kind === 'creating';
        const elsewhere = state.kind === 'idle' && state.elsewhere
          ? `
          <p class="krc-card-note krc-intake-hint" role="status">${escapeHtml(EMAIL_ELSEWHERE_NOTE)}</p>
          <button class="krc-text-button" type="button" data-email-action="resume">${escapeHtml(EMAIL_ELSEWHERE_ACTION)}</button>`
          : '';
        return `
          <p class="krc-card-text">카톡의 ‘대화 내보내기’를 전용 주소로 메일 보내면, 이 페이지가 받아서 바로 성적표를 만들어요. 파일을 따로 찾지 않아도 돼요.</p>${elsewhere}
          <button class="button ${primary}" type="button" data-email-action="create"${busy ? ' disabled aria-busy="true"' : ''}>${icon('mail')}<span>${busy ? '주소 만드는 중…' : '메일 주소 받기'}</span></button>
          <p class="krc-card-note">받은 파일은 이 기기로 옮기면 바로(늦어도 5분 뒤) 서버에서 지우고, 가져가지 않아도 주소를 받은 지 60분이 지나면 지워요. 보낸 사람 주소와 메일 내용은 저장하지 않아요. 만 14세 미만은 ‘파일로 올리기’를 이용해 주세요.</p>`;
      }
      case 'waiting': {
        const storageNote = intakeStorageNote(state.remembered);
        return `
          <div class="krc-address-box">
            <p class="krc-address-label" id="krc-address-label">이 주소로 메일을 보내 주세요</p>
            <p class="krc-address" id="krc-address" tabindex="-1" aria-describedby="krc-address-label">${addressMarkup(state.session.address)}</p>
            <button class="button button-primary krc-copy-button" type="button" data-email-action="copy">${icon('copy')}<span>주소 복사</span></button>
          </div>
          <div class="krc-waiting">
            <span class="krc-waiting-dot" aria-hidden="true"></span>
            <span class="krc-waiting-text">메일을 기다리는 중이에요</span>
            <span class="krc-waiting-time" data-email-remaining></span>
          </div>${storageNote ? `
          <p class="krc-card-note krc-intake-hint" data-email-storage-note>${escapeHtml(storageNote)}</p>` : ''}
          <p class="krc-connection-hint" data-email-connection hidden>연결이 잠시 불안정해요. 계속 확인하고 있어요.</p>
          <div class="krc-intake-failure" data-email-failure role="alert"></div>
          <div class="krc-tabs" role="tablist" aria-label="휴대폰 종류">
            ${PLATFORMS.map((item) => `<button type="button" role="tab" id="krc-email-tab-${item.id}" aria-controls="krc-email-panel-${item.id}" aria-selected="${item.id === platform}" tabindex="${item.id === platform ? 0 : -1}" data-email-platform="${item.id}">${item.label}</button>`).join('')}
          </div>
          ${PLATFORMS.map((item) => `<div class="krc-tab-panel" role="tabpanel" tabindex="0" id="krc-email-panel-${item.id}" aria-labelledby="krc-email-tab-${item.id}"${item.id === platform ? '' : ' hidden'}>${stepsMarkup(EMAIL_STEPS[item.id])}</div>`).join('')}
          <p class="krc-card-note">${escapeHtml(EMAIL_SENT_COPY_TIP)}</p>
          <button class="krc-text-button" type="button" data-email-action="cancel">그만두고 주소 지우기</button>`;
      }
      case 'expired':
        return `
          <p class="krc-intake-expired" role="alert">${escapeHtml(INTAKE_EXPIRED_MESSAGE)}</p>
          <button class="button ${primary}" type="button" data-email-action="create">${icon('refresh')}<span>새 주소 받기</span></button>`;
      case 'error':
        return `
          <p class="krc-intake-error" role="alert">${escapeHtml(state.message)}</p>
          <button class="button button-soft" type="button" data-email-action="create">${icon('refresh')}<span>다시 시도</span></button>`;
    }
  }

  function render(focus: FocusTarget): void {
    const hadFocus = container.contains(document.activeElement);
    container.dataset.emailState = state.kind;
    container.innerHTML = headerMarkup() + bodyMarkup();
    if (state.kind === 'waiting') updateWaiting();
    const target = focus ?? (hadFocus ? 'heading' : null);
    if (target === 'address') container.querySelector<HTMLElement>('#krc-address')?.focus();
    else if (target === 'primary') container.querySelector<HTMLElement>('[data-email-action="create"]')?.focus();
    else if (target === 'heading') container.querySelector<HTMLElement>('#krc-email-title')?.focus();
  }

  function setState(next: CardState, focus: FocusTarget = null): void {
    state = next;
    render(focus);
  }

  function updateRemaining(): void {
    const remaining = container.querySelector<HTMLElement>('[data-email-remaining]');
    if (remaining) remaining.textContent = `· ${intakeRemainingLabel(deadline, now())}`;
  }

  /** Only called when the failure changes: the box is an alert and re-announces on every write. */
  function updateFailure(): void {
    const box = container.querySelector<HTMLElement>('[data-email-failure]');
    if (!box) return;
    if (!failure) {
      box.hidden = true;
      box.textContent = '';
      return;
    }
    const copy = intakeFailureCopy(failure);
    box.hidden = false;
    box.innerHTML = `<strong>${icon('alert')}<span>보낸 메일에서 대화를 받지 못했어요</span></strong><p>${escapeHtml(copy.message)}</p>${copy.hint ? `<p class="krc-intake-hint">${escapeHtml(copy.hint)}</p>` : ''}`;
  }

  function updateWaiting(): void {
    updateRemaining();
    updateFailure();
  }

  function setConnectionTrouble(trouble: boolean): void {
    const hint = container.querySelector<HTMLElement>('[data-email-connection]');
    if (hint) hint.hidden = !trouble;
  }

  function stopWaiting(): void {
    poller?.stop();
    poller = null;
    window.clearInterval(ticker);
    ticker = 0;
  }

  /** The address is gone (server 404, or deadline + grace passed locally): forget only this one. */
  function expire(session: IntakeSession): void {
    stopWaiting();
    api.clear(session);
    trackKrc('krc_intake_failed', { reason: 'EXPIRED' });
    setState({ kind: 'expired' });
  }

  function onStatus(session: IntakeSession, status: IntakeStatusUpdate): void {
    if (disposed) return;
    if (status.state === 'received') {
      stopWaiting();
      options.onReceived(session, status);
      return;
    }
    if (status.state === 'expired') {
      expire(session);
      return;
    }
    // Derived from the server's expiresInMs on arrival, so it is right even on a wrong phone clock.
    if (Number.isFinite(status.deadline)) deadline = status.deadline;
    const next = status.lastFailure ?? null;
    if (next !== failure) {
      failure = next;
      if (failure) trackKrc('krc_intake_failed', { reason: failure });
      updateFailure();
    }
    setConnectionTrouble(false);
    updateRemaining();
  }

  function startWaiting(session: IntakeSession, focus: FocusTarget, remembered = true): void {
    stopWaiting();
    deadline = session.deadline;
    failure = null;
    setState({ kind: 'waiting', session, remembered }, focus);
    ticker = window.setInterval(updateRemaining, 15_000);
    poller = createIntakePoller({
      fetchStatus: async (signal) => {
        // Polling only happens while this page is visible, so the tab the
        // person is looking at takes the address over from any other tab.
        await api.take(session);
        return api.status(session, signal);
      },
      onStatus: (status) => onStatus(session, status),
      onError: (error, failures) => {
        if (disposed) return;
        if (classifyIntakeError(error) === 'gone') expire(session);
        else if (failures >= 2) setConnectionTrouble(true);
      },
      // Expires here even while the server cannot be reached (offline, service down).
      expiry: { deadline: () => deadline, onExpired: () => expire(session) },
      visibility,
      now,
    });
    poller.start();
  }

  /** Another open tab took this address over: step back without clearing or deleting it. */
  function onLost(sessionId: string): void {
    if (disposed || state.kind !== 'waiting' || state.session.id !== sessionId) return;
    const session = state.session;
    stopWaiting();
    setState({ kind: 'idle', elsewhere: session });
  }

  async function createSession(): Promise<void> {
    if (state.kind === 'creating' || disposed) return;
    setState({ kind: 'creating' }, 'primary');
    const controller = new AbortController();
    createController = controller;
    try {
      const session = await api.create(controller.signal);
      if (disposed || controller.signal.aborted) {
        // Nobody will wait for this address: delete it right away (retried if that fails).
        void api.discard(session);
        return;
      }
      // Without storage a reload loses the address: the waiting card says so.
      const remembered = api.save(session);
      trackKrc('krc_intake_started', null);
      startWaiting(session, 'address', remembered);
    } catch (error) {
      if (disposed || controller.signal.aborted) return;
      const kind = classifyIntakeError(error);
      trackKrc('krc_intake_failed', { reason: createFailureReason(kind) });
      setState({ kind: 'error', message: intakeErrorMessage(kind === 'gone' || kind === 'aborted' ? 'server' : kind) }, 'primary');
    } finally {
      if (createController === controller) createController = null;
    }
  }

  async function copyAddress(): Promise<void> {
    if (state.kind !== 'waiting') return;
    const address = state.session.address;
    if (await copyText(address)) {
      options.toast('주소를 복사했어요. 메일의 받는 사람 칸에 붙여넣어 주세요.');
      return;
    }
    const element = container.querySelector<HTMLElement>('#krc-address');
    if (element) selectElementText(element);
    options.toast('주소를 선택해 두었어요. 길게 눌러 복사해 주세요.');
  }

  /** '그만두고 주소 지우기': '지웠어요' only once the server confirmed it (a failed delete is retried). */
  function cancelSession(): void {
    if (state.kind !== 'waiting') return;
    const session = state.session;
    stopWaiting();
    void discardIntakeAddress(api, session, options.toast);
    setState(IDLE, 'primary');
  }

  /**
   * The tab that took the address over removes its stored entry once it has
   * the files, cancels or sees it expire. Drop the '다른 탭에서…' note then, so
   * '이 탭에서 이어서 기다리기' never points at an address that is gone.
   */
  function refreshElsewhere(): void {
    if (disposed || state.kind !== 'idle' || !state.elsewhere) return;
    const { id } = state.elsewhere;
    let found: StoredIntake;
    try {
      found = api.load();
    } catch {
      return;
    }
    if (found.session?.id !== id && found.elsewhere?.id !== id) setState(IDLE);
  }

  /** "이 탭에서 이어서 기다리기": the first poll takes the address back from the other tab. */
  function resumeHere(): void {
    if (state.kind !== 'idle' || !state.elsewhere) return;
    startWaiting(state.elsewhere, 'address');
  }

  function selectPlatform(next: PhonePlatform, focus: boolean): void {
    platform = next;
    container.querySelectorAll<HTMLButtonElement>('[data-email-platform]').forEach((tab) => {
      const selected = tab.dataset.emailPlatform === next;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });
    container.querySelectorAll<HTMLElement>('.krc-tab-panel').forEach((panel) => {
      panel.hidden = panel.id !== `krc-email-panel-${next}`;
    });
  }

  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    const tab = target?.closest<HTMLButtonElement>('[data-email-platform]');
    if (tab) {
      selectPlatform(tab.dataset.emailPlatform as PhonePlatform, false);
      return;
    }
    const action = target?.closest<HTMLElement>('[data-email-action]')?.dataset.emailAction;
    if (action === 'create') void createSession();
    else if (action === 'copy') void copyAddress();
    else if (action === 'cancel') cancelSession();
    else if (action === 'resume') resumeHere();
  };

  const onKeydown = (event: KeyboardEvent) => {
    const tab = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('[data-email-platform]') : null;
    if (!tab) return;
    const index = PLATFORMS.findIndex((item) => item.id === tab.dataset.emailPlatform);
    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % PLATFORMS.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + PLATFORMS.length) % PLATFORMS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = PLATFORMS.length - 1;
    if (next < 0) return;
    event.preventDefault();
    selectPlatform(PLATFORMS[next]!.id, true);
  };

  container.addEventListener('click', onClick);
  container.addEventListener('keydown', onKeydown);
  const stopListeningForLoss = api.onLost(onLost);
  // Fired in this tab when another tab changes localStorage, e.g. removes the address entry.
  window.addEventListener('storage', refreshElsewhere);

  let stored: StoredIntake = { session: null, elsewhere: null };
  try {
    stored = api.load();
  } catch {
    stored = { session: null, elsewhere: null };
  }
  const resumable = stored.session;
  if (resumable && now() > resumable.deadline + INTAKE_EXPIRY_GRACE_MS) {
    api.clear(resumable);
    setState({ kind: 'expired' });
  } else if (resumable) {
    startWaiting(resumable, null);
  } else {
    setState({ kind: 'idle', elsewhere: stored.elsewhere });
  }

  return {
    dispose() {
      disposed = true;
      stopWaiting();
      createController?.abort();
      stopListeningForLoss();
      window.removeEventListener('storage', refreshElsewhere);
      container.removeEventListener('click', onClick);
      container.removeEventListener('keydown', onKeydown);
    },
  };
}
